import React from "react";
import { Box } from "@mui/system";
import { SwitchToActivation } from "components";
import {
  ScorecardConfigKPI,
  ScorecardConfigCategory,
  ScorecardConfigSubtype,
  ScorecardConfigType,
} from "api";
import { hasDefinedRowValues } from "../../helpers/utils";
import { InputTextField } from "../inputTextField";
import { CustomDropdownField } from "../customDropdownField";
import { FieldConfig, FormFieldsProps } from "./types";
import { FIELD_STYLES, LAYOUT_STYLES } from "./styles";
import { getDefaultUnitOptions } from "./helpers";

export const FormFields: React.FC<FormFieldsProps> = ({
  formState,
  updateField,
  dropdownsData,
  isLastStep,
  rowData,
  isEditMode,
}) => {
  const {
    category,
    kpi,
    types,
    subtypes,
    treatmentMethod,
    class: kpiClass,
    defaultUnit,
    description,
    includeCostData,
    defaultCurrency,
  } = formState;

  // Disable custom option when user has already enter any value.
  const isUnitLocked =
    isEditMode && hasDefinedRowValues(rowData || null);

  const selectedTypeData = dropdownsData?.types?.find(
    (type: ScorecardConfigType) => type.type === types,
  );

  if (!isLastStep) {
    return (
      <CustomDropdownField
        label="Category"
        sxLabel={FIELD_STYLES.label}
        options={
          dropdownsData?.categories?.map((cat: any) => ({
            label: cat.categoryName,
            value: cat.categoryName,
          })) || []
        }
        selectedValue={category}
        onChange={(value) => updateField("category", value)}
        placeholder="Category"
        sx={{ width: "240px" }}
      />
    );
  }

  // Field configurations for Waste category
  const wasteFields: FieldConfig[] = [
    {
      key: "types",
      label: "Type",
      options:
        dropdownsData?.types?.map((type: ScorecardConfigType) => ({
          label: type.type,
          value: type.type,
        })) || [],
      selectedValue: types,
      placeholder: "Type",
      onChange: (value: string) => {
        updateField("types", value);
        updateField("subtypes", ""); // Reset subtypes when type changes
        if (!isUnitLocked) {
          updateField("defaultUnit", ""); // Reset default unit when type changes
        }
      },
    },
    {
      key: "subtypes",
      label: "Subtype",
      options:
        selectedTypeData?.subtypes?.map(
          (subtypeItem: ScorecardConfigSubtype) => ({
            label: subtypeItem.subtype,
            value: subtypeItem.subtype,
          }),
        ) || [],
      selectedValue: subtypes,
      placeholder: "Subtype",
      disabled: !selectedTypeData && !types,
      onChange: (value: string) => {
        updateField("subtypes", value);
        if (!isUnitLocked) updateField("defaultUnit", "");
      },
    },
    {
      key: "treatmentMethod",
      label: "Treatment Method",
      options:
        dropdownsData?.treatmentMethods?.map((method: string) => ({
          label: method,
          value: method,
        })) || [],
      selectedValue: treatmentMethod,
      placeholder: "Treatment Method",
      onChange: (value: string) => updateField("treatmentMethod", value),
    },
    {
      key: "class",
      label: "Class",
      options:
        dropdownsData?.classes?.map((classItem: string) => ({
          label: classItem,
          value: classItem,
        })) || [],
      selectedValue: kpiClass,
      placeholder: "Class",
      onChange: (value: string) => updateField("class", value),
    },
  ];

  // Field configurations for non-Waste categories
  const nonWasteFields: FieldConfig[] = [
    {
      key: "kpi",
      label: "KPI",
      options:
        dropdownsData?.categories
          ?.find(
            (cat: ScorecardConfigCategory) => cat.categoryName === category,
          )
          ?.kpis?.map((kpiItem: ScorecardConfigKPI) => ({
            label: kpiItem.name,
            value: kpiItem.name,
          })) || [],
      selectedValue: kpi,
      placeholder: "KPI",
      onChange: (value: string) => {
        updateField("kpi", value);
        if (!isUnitLocked) updateField("defaultUnit", "");
      },
    },
  ];

  // Common fields for all categories
  const commonFields: FieldConfig[] = [
    {
      key: "defaultUnit",
      label: "Default Unit",
      options: getDefaultUnitOptions(
        dropdownsData,
        category,
        kpi,
        types,
        subtypes,
      ).map((unitItem: string) => ({ label: unitItem, value: unitItem })),
      selectedValue: defaultUnit,
      placeholder: "Default Unit",
      onChange: (value: string) => updateField("defaultUnit", value),
    },
  ];

  // Conditional fields
  const conditionalFields: FieldConfig[] = [
    // Description field (only for Waste)
    ...(category === "Waste"
      ? [
          {
            key: "description",
            type: "text" as const,
            label: "Description",
            value: description,
            placeholder: "Description",
            onChange: (value: string) => updateField("description", value),
          },
        ]
      : []),
    // Include Cost Data switch
    {
      key: "includeCostData",
      type: "switch" as const,
      textLabel: "Include Cost Data",
      enabled: includeCostData,
      onChange: () => updateField("includeCostData", !includeCostData),
    },
    // Default Currency (only when includeCostData is true)
    ...(includeCostData
      ? [
          {
            key: "defaultCurrency",
            label: "Default Currency",
            options:
              dropdownsData?.defaultCurrencies?.map((currency: string) => ({
                label: currency,
                value: currency,
              })) || [],
            selectedValue: defaultCurrency,
            placeholder: "Default Currency",
            onChange: (value: string) => updateField("defaultCurrency", value),
            showCustomOption: false,
          },
        ]
      : []),
  ];

  const renderSelectField = (field: FieldConfig) => (
    <CustomDropdownField
      key={field.key}
      sxMenuItem={FIELD_STYLES.menuItem}
      sxSelect={FIELD_STYLES.select}
      sx={{ width: "100%" }}
      label={field.label || ""}
      sxLabel={FIELD_STYLES.label}
      options={field.options || []}
      selectedValue={field.selectedValue || ""}
      onChange={(value) => field.onChange(value)}
      placeholder={field.placeholder || ""}
      disabled={
        field.key === "defaultUnit"
          ? isUnitLocked || field.disabled
          : field.disabled
      }
      showCustomOption={field.showCustomOption}
      disabledTooltip={
        field.key === "defaultUnit" && isUnitLocked
          ? "This row has existing data entries recorded in this unit. Remove all data entries to change the default unit."
          : undefined
      }
    />
  );

  const renderTextField = (field: FieldConfig) => (
    <InputTextField field={field} />
  );

  const renderSwitchField = (field: FieldConfig) => (
    <SwitchToActivation
      key={field.key}
      textLabel={field.textLabel}
      sxLabel={FIELD_STYLES.switchLabel}
      enabled={field.enabled || false}
      textOn="Enabled"
      textOff="Disabled"
      onChange={() => field.onChange("")}
    />
  );

  const renderField = (field: FieldConfig) => {
    switch (field.type) {
      case "text":
        return renderTextField(field);
      case "switch":
        return renderSwitchField(field);
      default:
        return renderSelectField(field);
    }
  };

  // Determine which fields to render based on category
  const fieldsToRender =
    category === "Waste"
      ? [...wasteFields, ...commonFields, ...conditionalFields]
      : [...nonWasteFields, ...commonFields, ...conditionalFields];

  return (
    <Box sx={LAYOUT_STYLES.gridContainer}>
      {fieldsToRender.map(renderField)}
    </Box>
  );
};
